import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { CreateDocumentModal, CreateTemplateModal, CreateCaseModal } from '../components/LegalModals';
import { formatDate } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = ['Overview', 'Franchisee Agreements', 'Company Legal', 'Templates', 'Cases'];

function Legal({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('Overview');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agreements, setAgreements] = useState([]);
  const [agreementStats, setAgreementStats] = useState(null);
  const [selectedAgreementId, setSelectedAgreementId] = useState(null);
  
  // Modal states
  const [showCreateDocModal, setShowCreateDocModal] = useState(false);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);
  
  // Data states for new tabs
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [cases, setCases] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchAgreementStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'Franchisee Agreements') {
      fetchAgreements();
    } else if (activeTab === 'Company Legal') {
      fetchDocuments();
    } else if (activeTab === 'Templates') {
      fetchTemplates();
    } else if (activeTab === 'Cases') {
      fetchCases();
    }
  }, [activeTab]);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/legal/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/legal/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchCases = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/legal/cases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCases(response.data.cases || []);
    } catch (error) {
      console.error('Failed to fetch cases:', error);
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/legal/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch legal stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgreementStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agreements/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgreementStats(response.data);
    } catch (error) {
      console.error('Failed to fetch agreement stats:', error);
    }
  };

  const fetchAgreements = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agreements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgreements(response.data.agreements || []);
    } catch (error) {
      console.error('Failed to fetch agreements:', error);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Legal & Compliance</h1>
          {(user.role === 'Admin' || user.role === 'HR Manager') && (
            <div className="flex gap-3">
              <button
                onClick={() => setShowCreateTemplateModal(true)}
                className="px-4 py-2 rounded-lg text-white text-sm hover:opacity-90"
                style={{backgroundColor: '#673ab7'}}
              >
                + New Template
              </button>
              <button
                onClick={() => setShowCreateDocModal(true)}
                className="px-4 py-2 rounded-lg text-white text-sm hover:opacity-90"
                style={{backgroundColor: '#2b3380'}}
              >
                + New Document
              </button>
              <button
                onClick={() => setShowCreateCaseModal(true)}
                className="px-4 py-2 rounded-lg text-white text-sm hover:opacity-90"
                style={{backgroundColor: '#e91e63'}}
              >
                + New Case
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="flex border-b">
            {TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 font-medium transition ${
                  activeTab === tab
                    ? 'border-b-2 text-[#2b3380]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                style={activeTab === tab ? {borderBottomColor: '#2b3380'} : {}}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'Overview' && !loading && stats && (
          <>
            {/* Legal Dashboard */}
            <MiniDashboard
              title="Legal & Compliance Overview"
              icon="⚖️"
              bgColor="#673ab7"
              stats={[
                {
                  label: 'Total Documents',
                  value: stats.total_documents || 0,
                  subtitle: 'All legal documents',
                  color: '#673ab7'
                },
                {
                  label: 'Pending Signatures',
                  value: stats.pending_signatures || 0,
                  subtitle: 'Awaiting signature',
                  color: '#ff9800'
                },
                {
                  label: 'Active Templates',
                  value: stats.total_templates || 0,
                  subtitle: 'Document templates',
                  color: '#2196f3'
                },
                {
                  label: 'Open Cases',
                  value: stats.open_cases || 0,
                  subtitle: 'Active legal cases',
                  color: '#e91e63'
                }
              ]}
            />

            {/* Secondary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-800">Cases Overview</h3>
                  <span className="text-3xl">📋</span>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total Cases</span>
                    <span className="font-bold text-gray-900">{stats.total_cases || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Open Cases</span>
                    <span className="font-bold text-orange-600">{stats.open_cases || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">High Priority</span>
                    <span className="font-bold text-red-600">{stats.high_priority_cases || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Resolved</span>
                    <span className="font-bold text-green-600">{stats.resolved_cases || 0}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-800">Document Status</h3>
                  <span className="text-3xl">📄</span>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total Documents</span>
                    <span className="font-bold text-gray-900">{stats.total_documents || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Pending Signature</span>
                    <span className="font-bold text-orange-600">{stats.pending_signatures || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Signed</span>
                    <span className="font-bold text-green-600">{stats.signed_documents || 0}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-800">Documents by Category</h3>
                  <span className="text-3xl">📊</span>
                </div>
                <div className="space-y-3">
                  {stats.documents_by_category && Object.entries(stats.documents_by_category).map(([category, count]) => (
                    <div key={category} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{category}</span>
                      <span className="font-bold text-gray-900">{count}</span>
                    </div>
                  ))}
                  {(!stats.documents_by_category || Object.keys(stats.documents_by_category).length === 0) && (
                    <p className="text-sm text-gray-500 text-center py-4">No documents yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg shadow-lg p-6 mt-6 text-white">
              <h3 className="text-xl font-bold mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => setActiveTab('Documents')}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg p-4 text-left transition"
                >
                  <div className="text-2xl mb-2">📝</div>
                  <div className="font-semibold">Generate Document</div>
                  <div className="text-sm opacity-90">Create from template</div>
                </button>
                <button
                  onClick={() => setActiveTab('Cases')}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg p-4 text-left transition"
                >
                  <div className="text-2xl mb-2">⚠️</div>
                  <div className="font-semibold">Report Issue</div>
                  <div className="text-sm opacity-90">Create new case</div>
                </button>
                <button
                  onClick={() => setActiveTab('Templates')}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg p-4 text-left transition"
                >
                  <div className="text-2xl mb-2">📋</div>
                  <div className="font-semibold">Manage Templates</div>
                  <div className="text-sm opacity-90">View all templates</div>
                </button>
              </div>
            </div>

            {/* Franchisee Agreements Stats (Added to Overview) */}
            {agreementStats && (
              <div className="mt-6">
                <h2 className="text-xl font-bold mb-4 text-gray-800">Franchisee Agreements Overview</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="text-sm text-gray-600 mb-2">Total Agreements</div>
                    <div className="text-3xl font-bold text-[#2b3380]">{agreementStats.total_agreements || 0}</div>
                  </div>
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="text-sm text-gray-600 mb-2">Active</div>
                    <div className="text-3xl font-bold text-green-600">{agreementStats.status_breakdown?.active || 0}</div>
                  </div>
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="text-sm text-gray-600 mb-2">Pending Signature</div>
                    <div className="text-3xl font-bold text-orange-600">{agreementStats.status_breakdown?.pending_signature || 0}</div>
                  </div>
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="text-sm text-gray-600 mb-2">Draft</div>
                    <div className="text-3xl font-bold text-gray-600">{agreementStats.status_breakdown?.draft || 0}</div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Franchisee Agreements Tab */}
        {activeTab === 'Franchisee Agreements' && (
          <FranchiseeAgreementsTab 
            agreements={agreements} 
            onRefresh={fetchAgreements}
            onView={(id) => setSelectedAgreementId(id)}
          />
        )}

        {/* Company Legal Tab */}
        {activeTab === 'Company Legal' && (
          <DocumentsTab documents={documents} onRefresh={fetchDocuments} />
        )}

        {/* Templates Tab */}
        {activeTab === 'Templates' && (
          <TemplatesTab templates={templates} onRefresh={fetchTemplates} />
        )}

        {/* Cases Tab */}
        {activeTab === 'Cases' && (
          <CasesTab cases={cases} onRefresh={fetchCases} />
        )}

        {loading && (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-gray-600">Loading...</p>
          </div>
        )}

        {/* Agreement Detail Modal */}
        {selectedAgreementId && (
          <AgreementDetailModal
            agreementId={selectedAgreementId}
            onClose={() => setSelectedAgreementId(null)}
            onUpdate={() => {
              setSelectedAgreementId(null);
              fetchAgreements();
              fetchAgreementStats();
            }}
          />
        )}

        {/* Create Document Modal */}
        <CreateDocumentModal
          isOpen={showCreateDocModal}
          onClose={() => setShowCreateDocModal(false)}
          onSuccess={() => {
            fetchDocuments();
            fetchStats();
            setShowCreateDocModal(false);
          }}
        />

        {/* Create Template Modal */}
        <CreateTemplateModal
          isOpen={showCreateTemplateModal}
          onClose={() => setShowCreateTemplateModal(false)}
          onSuccess={() => {
            fetchTemplates();
            fetchStats();
            setShowCreateTemplateModal(false);
          }}
        />

        {/* Create Case Modal */}
        <CreateCaseModal
          isOpen={showCreateCaseModal}
          onClose={() => setShowCreateCaseModal(false)}
          onSuccess={() => {
            fetchCases();
            fetchStats();
            setShowCreateCaseModal(false);
          }}
        />
      </div>
    </Layout>
  );
}

// ==================== FRANCHISEE AGREEMENTS TAB ====================
function FranchiseeAgreementsTab({ agreements, onRefresh, onView }) {
  const getStatusBadge = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Pending Signature': 'bg-yellow-100 text-yellow-800',
      'Signed': 'bg-[#2b338030] text-[#2b3380]',
      'Active': 'bg-green-100 text-green-800',
      'Expired': 'bg-red-100 text-red-800',
      'Terminated': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  return (
    <div>
      <div className="flex justify-between mb-4">
        <h3 className="text-lg font-bold">All Franchisee Agreements</h3>
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660]"
        >
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agreement #</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Store</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applicant</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Franchise Fee</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {agreements.map((agreement) => (
              <tr key={agreement.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-[#2b3380]">{agreement.agreement_number}</td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">{agreement.store_name}</div>
                  <div className="text-sm text-gray-500">{agreement.store_code}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{agreement.applicant_name}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{agreement.store_type}</td>
                <td className="px-6 py-4 text-sm text-gray-900 text-right">₹{agreement.franchise_fee?.toLocaleString()}</td>
                <td className="px-6 py-4">{getStatusBadge(agreement.status)}</td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={() => onView(agreement.id)}
                    className="text-[#2b3380] hover:text-blue-900 text-sm font-medium"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {agreements.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No agreements found. Create agreements from approved leads!
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== AGREEMENT DETAIL MODAL ====================
function AgreementDetailModal({ agreementId, onClose, onUpdate }) {
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fees, setFees] = useState([]);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    fetchDetails();
  }, [agreementId]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agreements/${agreementId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgreement(response.data.agreement);
      setFees(response.data.fees || []);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching agreement details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  if (!agreement) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{agreement.agreement_number}</h2>
            <p className="text-gray-600">{agreement.store_name} • {agreement.applicant_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold mb-3">Agreement Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Store Type:</span>
                <span className="ml-2 font-medium">{agreement.store_type}</span>
              </div>
              <div>
                <span className="text-gray-600">Model:</span>
                <span className="ml-2 font-medium">{agreement.ownership_model}</span>
              </div>
              <div>
                <span className="text-gray-600">Territory:</span>
                <span className="ml-2 font-medium">{agreement.territory}</span>
              </div>
              <div>
                <span className="text-gray-600">Duration:</span>
                <span className="ml-2 font-medium">{agreement.duration_months} months</span>
              </div>
            </div>
          </div>

          {/* Financial Terms */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold mb-3">Financial Terms</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Franchise Fee:</span>
                <span className="ml-2 font-medium text-lg">₹{agreement.franchise_fee?.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-600">Security Deposit:</span>
                <span className="ml-2 font-medium text-lg">₹{agreement.security_deposit?.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-600">Royalty:</span>
                <span className="ml-2 font-medium">{agreement.royalty_percentage}%</span>
              </div>
              <div>
                <span className="text-gray-600">Marketing Fee:</span>
                <span className="ml-2 font-medium">{agreement.marketing_fee_percentage}%</span>
              </div>
            </div>
          </div>

          {/* Signature Status */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold mb-3">Signature Status</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span>Franchisee:</span>
                <span className={agreement.franchisee_signed ? 'text-green-600 font-medium' : 'text-gray-500'}>
                  {agreement.franchisee_signed ? '✓ Signed' : '✗ Pending'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Company:</span>
                <span className={agreement.company_signed ? 'text-green-600 font-medium' : 'text-gray-500'}>
                  {agreement.company_signed ? '✓ Signed' : '✗ Pending'}
                </span>
              </div>
            </div>
          </div>

          {/* Fee Structure */}
          {fees.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3">Fee Structure</h3>
              <div className="space-y-2">
                {fees.map(fee => (
                  <div key={fee.id} className="bg-white border rounded p-3 text-sm">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-medium">{fee.fee_name}</div>
                        <div className="text-gray-500 text-xs">Due: {new Date(fee.due_date).toLocaleDateString()}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">₹{fee.total_amount?.toLocaleString()}</div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          fee.payment_status === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {fee.payment_status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment History */}
          {transactions.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3">Payment History</h3>
              <div className="space-y-2">
                {transactions.map(txn => (
                  <div key={txn.id} className="bg-white border rounded p-3 text-sm">
                    <div className="flex justify-between">
                      <div>
                        <div className="font-medium">{txn.transaction_number}</div>
                        <div className="text-gray-500 text-xs">{txn.payment_method} • {new Date(txn.transaction_date).toLocaleDateString()}</div>
                      </div>
                      <div className="font-bold text-green-600">₹{txn.amount?.toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== DOCUMENTS TAB ====================
function DocumentsTab({ documents, onRefresh }) {
  const getStatusBadge = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Pending Signature': 'bg-yellow-100 text-yellow-800',
      'Signed': 'bg-green-100 text-green-800',
      'Approved': 'bg-blue-100 text-blue-800',
      'Rejected': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  return (
    <div>
      <div className="flex justify-between mb-4">
        <h3 className="text-lg font-bold">Legal Documents</h3>
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660]"
        >
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Document #</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Party</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Effective Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {documents.map((doc) => (
              <tr key={doc.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-[#2b3380]">{doc.document_number}</td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">{doc.document_name}</div>
                  {doc.notes && <div className="text-sm text-gray-500">{doc.notes.substring(0, 50)}...</div>}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{doc.category}</td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">{doc.party_name}</div>
                  <div className="text-sm text-gray-500">{doc.party_email}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {doc.effective_date ? new Date(doc.effective_date).toLocaleDateString() : '-'}
                </td>
                <td className="px-6 py-4">{getStatusBadge(doc.status)}</td>
                <td className="px-6 py-4 text-right">
                  <button className="text-[#2b3380] hover:text-blue-900 text-sm font-medium">
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {documents.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No documents found. Create your first document!
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== TEMPLATES TAB ====================
function TemplatesTab({ templates, onRefresh }) {
  return (
    <div>
      <div className="flex justify-between mb-4">
        <h3 className="text-lg font-bold">Document Templates</h3>
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660]"
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((template) => (
          <div key={template.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition">
            <div className="flex items-start justify-between mb-3">
              <div className="text-3xl mb-2">📄</div>
              <span className={`text-xs px-2 py-1 rounded-full ${
                template.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {template.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <h3 className="font-semibold text-lg mb-2 text-gray-900">{template.template_name}</h3>
            <p className="text-sm text-gray-600 mb-3">{template.description}</p>
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded">{template.category}</span>
              <span>v{template.version}</span>
            </div>
            <div className="mt-4 pt-4 border-t flex justify-between">
              <button className="text-[#2b3380] hover:text-blue-900 text-sm font-medium">
                View
              </button>
              <button className="text-gray-600 hover:text-gray-900 text-sm font-medium">
                Edit
              </button>
            </div>
          </div>
        ))}
        {templates.length === 0 && (
          <div className="col-span-3 text-center py-12 text-gray-500">
            No templates found. Create your first template!
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== CASES TAB ====================
function CasesTab({ cases, onRefresh }) {
  const getStatusBadge = (status) => {
    const colors = {
      'Open': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Investigation': 'bg-orange-100 text-orange-800',
      'Resolved': 'bg-green-100 text-green-800',
      'Closed': 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      'Low': 'bg-green-100 text-green-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'High': 'bg-orange-100 text-orange-800',
      'Critical': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[priority] || 'bg-gray-100 text-gray-800'}`}>
        {priority}
      </span>
    );
  };

  return (
    <div>
      <div className="flex justify-between mb-4">
        <h3 className="text-lg font-bold">Legal Cases & Escalations</h3>
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660]"
        >
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Case #</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Party</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {cases.map((caseItem) => (
              <tr key={caseItem.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-[#2b3380]">{caseItem.case_number}</td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">{caseItem.case_title}</div>
                  <div className="text-sm text-gray-500">{caseItem.description.substring(0, 60)}...</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{caseItem.case_type}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{caseItem.party_involved || '-'}</td>
                <td className="px-6 py-4">{getPriorityBadge(caseItem.priority)}</td>
                <td className="px-6 py-4">{getStatusBadge(caseItem.status)}</td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {caseItem.due_date ? new Date(caseItem.due_date).toLocaleDateString() : '-'}
                </td>
                <td className="px-6 py-4 text-right">
                  <button className="text-[#2b3380] hover:text-blue-900 text-sm font-medium">
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {cases.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No cases found. All clear!
          </div>
        )}
      </div>
    </div>
  );
}

export default Legal;
